
<script setup>

  import { ref, watch } from 'vue';

  import { useResizeObserver, useDebounceFn } from '@vueuse/core';

  const props = defineProps( { src: String } );

  const wrapper = ref( null );

  const canvas = ref( null );

  const buffer = ref( null );

  const width = ref( 0 );

  const height = ref( 0 );

  const pixelRatio = window.devicePixelRatio || 1;

  function fetchBuffer() {

    fetch( props.src )

      .then( data => data.arrayBuffer() )

      .then( audioData => {
      
        const context = new AudioContext();

        return context.decodeAudioData( audioData );
      
      } )

      .then( audioBuffer => buffer.value = audioBuffer )

      .catch( () => buffer.value = null );

  }

  const drawBuffer = useDebounceFn( () => {

    if ( ! canvas.value || ! buffer.value ) return;

    const css = window.getComputedStyle( canvas.value, null );

    const context = canvas.value.getContext( '2d' );

    const center = height.value / 2;

    const data = buffer.value.getChannelData( 0 );

    const size = Math.ceil( data.length / width.value );

    const samples = Array.from( { length: width.value }, ( _, index ) =>
    
      data.slice( index * size, index * size + size )
      
        .reduce( ( sum, value ) => sum + Math.abs( value, 0 ) ) / size );

    const max = samples.reduce( ( max, value ) => Math.max( max, value ), 0 );

    context.fillStyle = css.getPropertyValue( 'color' ); /* currentColor would be great, if it worked */

    samples.forEach( ( value, index ) => {
      
      const scaled = value / max * height.value;
      
      context.fillRect( index, center - scaled / 2, 1, scaled );
    
    } );

  } );

  watch( () => props.src, fetchBuffer, { immediate: true } );

  watch( [ wrapper, canvas, buffer, width, height ], () => drawBuffer() );

  useResizeObserver( wrapper, () => setTimeout( () => {

    const { width: scaledWidth, height: scaledHeight } = wrapper.value.getBoundingClientRect();

    width.value = scaledWidth * pixelRatio;

    height.value = scaledHeight * pixelRatio;

  }, 250 ) );

</script>

<template>

  <div ref="wrapper" class="overflow-hidden min-h-[60px]">

    <canvas ref="canvas" :width="width" :height="height"
      
      :style="{ width: `${ width / pixelRatio }px`, height: `${ height / pixelRatio }px` }" />

  </div>

</template>
